import React, { useCallback, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@roger/ui-components';

interface HotboxTaskNotesDialogProps {
  open: boolean;
  draftNotes: string;
  confirmedNotes: string;
  isSaving: boolean;
  error: string | null;
  onDraftNotesChange: (notes: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const HotboxTaskNotesDialog: React.FC<HotboxTaskNotesDialogProps> = ({
  open,
  draftNotes,
  confirmedNotes,
  isSaving,
  error,
  onDraftNotesChange,
  onSave,
  onCancel,
}) => {
  const saveDisabled = isSaving || draftNotes === confirmedNotes;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const wasOpenRef = useRef(false);
  const cursorPlacementPendingRef = useRef(false);

  const placeCursorAtEnd = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPosition = textarea.value.length;
    textarea.focus();
    textarea.setSelectionRange(cursorPosition, cursorPosition);
    if (document.activeElement === textarea) {
      cursorPlacementPendingRef.current = false;
    }
  }, []);

  const focusTextareaAtEnd = useCallback(() => {
    cursorPlacementPendingRef.current = true;
    window.requestAnimationFrame(placeCursorAtEnd);
    window.setTimeout(placeCursorAtEnd, 0);
    window.setTimeout(placeCursorAtEnd, 50);
  }, [placeCursorAtEnd]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      focusTextareaAtEnd();
    }
    if (!open) {
      cursorPlacementPendingRef.current = false;
    }
    wasOpenRef.current = open;
  }, [focusTextareaAtEnd, open]);

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen && !isSaving) {
          onCancel();
        }
      }}
    >
      <DialogContent
        onOpenAutoFocus={event => {
          event.preventDefault();
          focusTextareaAtEnd();
        }}
        onEscapeKeyDown={event => {
          if (isSaving) {
            event.preventDefault();
          }
        }}
        onInteractOutside={event => {
          if (isSaving) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Task Notes</DialogTitle>
          <DialogDescription>
            Notes are visible to everyone who handles this chart.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-2'>
          <Label htmlFor='hotbox-task-notes'>Task notes</Label>
          <Textarea
            ref={textareaRef}
            id='hotbox-task-notes'
            aria-label='Task notes'
            value={draftNotes}
            onFocus={event => {
              if (cursorPlacementPendingRef.current) {
                const cursorPosition = event.currentTarget.value.length;
                event.currentTarget.setSelectionRange(
                  cursorPosition,
                  cursorPosition
                );
                cursorPlacementPendingRef.current = false;
              }
            }}
            onChange={event => onDraftNotesChange(event.target.value)}
            disabled={isSaving}
            textareaSize='smNarrative'
            className='whitespace-pre-wrap'
          />
        </div>

        {error && (
          <p className='text-sm text-destructive' role='alert'>
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saveDisabled}>
            {isSaving ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Saving...
              </>
            ) : (
              'Save Notes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
